import { Address } from '@/lib/types/address';
import { OrderActions } from '@/lib/context/orderActions';
import { OrderState } from '@/lib/types/orderState';
import { ensurePartyOrderFulfillment } from '@/lib/functions/ensurePartyOrderFulfillment';
import {
  ensurePartyOrderSubstitutions,
  resolvePartyAllowSubstitutionsForCartUpdate,
} from '@/lib/functions/ensurePartyOrderSubstitutions';
import { getOrderByToken } from '@/lib/functions/getOrderByToken';
import {
  describeOrderShippingSnapshot,
  logShippingEstimate,
} from '@/lib/functions/logShippingEstimate';
import { orderHasShippingEstimate } from '@/lib/functions/orderHasShippingEstimate';
import { isShippingQuoteUnavailable } from '@/lib/functions/isShippingQuoteUnavailable';
import { resolveDefaultPartyShippingMethodCode } from '@/lib/functions/preferedShippingMethods';
import { setOrderAddress } from '@/lib/functions/setOrderAddress';
import { setOrderShippingMethod } from '@/lib/functions/setOrderShippingMethod';

import type { OrderPayload } from '@/lib/types/orderPayload';

const DEFAULT_UNSHIPABLE_MESSAGE =
  'We are unable to ship to this address. Some items in your cart may have shipping restrictions for your state or ZIP code.';

const SHIPPING_UNAVAILABLE_MESSAGE =
  'Shipping is unavailable for this order. An item may be too large to ship or cannot ship to your address.';

const SHIPPING_ESTIMATE_POLL_ATTEMPTS = 5;
const SHIPPING_ESTIMATE_POLL_DELAY_MS = 500;

export type ShippingEstimateFailureStep =
  | 'missing_token'
  | 'address_update_failed'
  | 'missing_shipment'
  | 'shipment_patch_failed';

export interface EstimateOrderShippingResult {
  success: boolean;
  message: string;
  failureStep?: ShippingEstimateFailureStep;
  order?: OrderPayload | null;
}

function delay(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

function shippingUnavailableFailure(order: OrderPayload | null | undefined): EstimateOrderShippingResult {
  logShippingEstimate('failure', {
    step: 'shipping_unavailable',
    ...describeOrderShippingSnapshot(order),
  });

  return {
    success: false,
    message: SHIPPING_UNAVAILABLE_MESSAGE,
    failureStep: 'shipment_patch_failed',
    order,
  };
}

async function finishIfOrderAlreadyQuoted(
  orderState: OrderState,
  orderDispatch: React.Dispatch<OrderActions>,
  order: OrderPayload | null | undefined,
  via: string,
): Promise<EstimateOrderShippingResult | null> {
  if (!order || !orderHasShippingEstimate(order)) {
    return null;
  }

  let updatedOrder = order;
  if (orderState.token) {
    const substitutionsUpdated = await ensurePartyOrderSubstitutions(orderState.token, updatedOrder);
    if (substitutionsUpdated) {
      updatedOrder = await getOrderByToken(orderState, orderDispatch) ?? updatedOrder;
    }
  }

  if (isShippingQuoteUnavailable(updatedOrder.pricing?.shipping)) {
    return shippingUnavailableFailure(updatedOrder);
  }

  logShippingEstimate('success', {
    via,
    ...describeOrderShippingSnapshot(updatedOrder),
  });

  return { success: true, message: '', order: updatedOrder };
}

async function waitForShipmentId(
  orderState: OrderState,
  orderDispatch: React.Dispatch<OrderActions>,
): Promise<OrderPayload | null> {
  for (let attempt = 0; attempt < SHIPPING_ESTIMATE_POLL_ATTEMPTS; attempt++) {
    const order = await getOrderByToken(orderState, orderDispatch);
    const shipmentId = order?.shipments?.[0]?.id;

    logShippingEstimate('poll_shipment', {
      attempt: attempt + 1,
      shipmentId: shipmentId ?? null,
      ...describeOrderShippingSnapshot(order),
    });

    if (shipmentId) {
      return order;
    }

    if (attempt < SHIPPING_ESTIMATE_POLL_ATTEMPTS - 1) {
      await delay(SHIPPING_ESTIMATE_POLL_DELAY_MS);
    }
  }

  return null;
}

async function waitForShippingQuote(
  orderState: OrderState,
  orderDispatch: React.Dispatch<OrderActions>,
  initialOrder?: OrderPayload | null,
): Promise<OrderPayload | null> {
  for (let attempt = 0; attempt < SHIPPING_ESTIMATE_POLL_ATTEMPTS; attempt++) {
    const order = attempt === 0 && initialOrder
      ? initialOrder
      : await getOrderByToken(orderState, orderDispatch);

    logShippingEstimate('poll_quote', {
      attempt: attempt + 1,
      hasEstimate: orderHasShippingEstimate(order),
      ...describeOrderShippingSnapshot(order),
    });

    if (order && isShippingQuoteUnavailable(order.pricing?.shipping)) {
      return order;
    }

    if (order && orderHasShippingEstimate(order)) {
      return order;
    }

    if (attempt < SHIPPING_ESTIMATE_POLL_ATTEMPTS - 1) {
      await delay(SHIPPING_ESTIMATE_POLL_DELAY_MS);
    }
  }

  return null;
}

/**
 * Sets the order address and returns whether shipping was quoted.
 * Relies on Sylius order processing from the address PUT; does not fetch the methods list.
 */
export async function estimateOrderShipping(
  orderState: OrderState,
  orderDispatch: React.Dispatch<OrderActions>,
  shippingAddress: Address,
): Promise<EstimateOrderShippingResult> {
  if (!orderState?.token) {
    logShippingEstimate('failure', { step: 'missing_token' });
    return {
      success: false,
      message: 'Add an item to your cart before estimating shipping.',
      failureStep: 'missing_token',
    };
  }

  logShippingEstimate('start', {
    token: orderState.token,
    province: shippingAddress.provinceCode,
    postcode: shippingAddress.postcode,
    country: shippingAddress.countryCode,
  });

  const currentOrder = await getOrderByToken(orderState, orderDispatch);
  const allowSubstitutions = resolvePartyAllowSubstitutionsForCartUpdate(
    orderState.token,
    currentOrder?.settings?.allowSubstitutions,
  );

  logShippingEstimate('initial_order', {
    allowSubstitutions,
    ...describeOrderShippingSnapshot(currentOrder),
  });

  const addressUpdateResponse = await setOrderAddress(
    orderState,
    '',
    shippingAddress,
    shippingAddress,
    undefined,
    allowSubstitutions,
  );

  if (!addressUpdateResponse.success) {
    logShippingEstimate('failure', {
      step: 'address_update_failed',
      message: addressUpdateResponse.message,
    });
    return {
      success: false,
      message:
        addressUpdateResponse.message ||
        'We could not validate this address. Please check your information and try again.',
      failureStep: 'address_update_failed',
    };
  }

  let order = await getOrderByToken(orderState, orderDispatch);
  logShippingEstimate('after_address', describeOrderShippingSnapshot(order));

  const quotedAfterAddress = await finishIfOrderAlreadyQuoted(
    orderState,
    orderDispatch,
    order,
    'after_address',
  );
  if (quotedAfterAddress) {
    return quotedAfterAddress;
  }

  const fulfillmentUpdated = await ensurePartyOrderFulfillment(orderState, orderDispatch, order);
  if (fulfillmentUpdated) {
    logShippingEstimate('fulfillment_updated');
    order = await getOrderByToken(orderState, orderDispatch);
    logShippingEstimate('after_fulfillment', describeOrderShippingSnapshot(order));

    const quotedAfterFulfillment = await finishIfOrderAlreadyQuoted(
      orderState,
      orderDispatch,
      order,
      'after_fulfillment',
    );
    if (quotedAfterFulfillment) {
      return quotedAfterFulfillment;
    }
  }

  let shipmentId = order?.shipments?.[0]?.id;
  if (!shipmentId) {
    logShippingEstimate('shipment_missing_polling');
    order = await waitForShipmentId(orderState, orderDispatch);
    shipmentId = order?.shipments?.[0]?.id;
  }

  if (!shipmentId) {
    logShippingEstimate('failure', {
      step: 'missing_shipment',
      ...describeOrderShippingSnapshot(order),
    });
    return {
      success: false,
      message: DEFAULT_UNSHIPABLE_MESSAGE,
      failureStep: 'missing_shipment',
    };
  }

  const quotedAfterPoll = await waitForShippingQuote(orderState, orderDispatch, order);
  if (quotedAfterPoll) {
    if (isShippingQuoteUnavailable(quotedAfterPoll.pricing?.shipping)) {
      return shippingUnavailableFailure(quotedAfterPoll);
    }

    const quotedResult = await finishIfOrderAlreadyQuoted(
      orderState,
      orderDispatch,
      quotedAfterPoll,
      'after_quote_poll',
    );
    if (quotedResult) {
      return quotedResult;
    }
  }

  const defaultMethodCode = resolveDefaultPartyShippingMethodCode(shippingAddress.countryCode);

  logShippingEstimate('assign_method', {
    shipmentId,
    methodCode: defaultMethodCode,
  });

  const shipmentResponse = await setOrderShippingMethod(
    orderState.token,
    shipmentId,
    defaultMethodCode,
  );

  if (!shipmentResponse.success) {
    logShippingEstimate('failure', {
      step: 'shipment_patch_failed',
      shipmentId,
      methodCode: defaultMethodCode,
      message: shipmentResponse.message,
    });
    return {
      success: false,
      message:
        shipmentResponse.message ||
        'We could not calculate shipping for this address. Please try a different address.',
      failureStep: 'shipment_patch_failed',
    };
  }

  let updatedOrder = await getOrderByToken(orderState, orderDispatch);
  const substitutionsUpdated = await ensurePartyOrderSubstitutions(orderState.token, updatedOrder);
  if (substitutionsUpdated) {
    logShippingEstimate('substitutions_reprocessed');
    updatedOrder = await getOrderByToken(orderState, orderDispatch);
  }

  logShippingEstimate('after_patch', {
    methodAssigned: defaultMethodCode,
    hasEstimate: orderHasShippingEstimate(updatedOrder),
    ...describeOrderShippingSnapshot(updatedOrder),
  });

  if (isShippingQuoteUnavailable(updatedOrder?.pricing?.shipping)) {
    return shippingUnavailableFailure(updatedOrder);
  }

  // PATCH success is authoritative; pricing/method on the next GET can lag FedEx re-quote.
  if (orderHasShippingEstimate(updatedOrder)) {
    logShippingEstimate('success', { via: 'immediate_order_snapshot' });
    return { success: true, message: '', order: updatedOrder };
  }

  for (let attempt = 0; attempt < SHIPPING_ESTIMATE_POLL_ATTEMPTS; attempt++) {
    if (attempt > 0) {
      await delay(SHIPPING_ESTIMATE_POLL_DELAY_MS);
    }

    updatedOrder = await getOrderByToken(orderState, orderDispatch);
    const hasEstimate = orderHasShippingEstimate(updatedOrder);

    logShippingEstimate('poll_post_patch', {
      attempt: attempt + 1,
      hasEstimate,
      ...describeOrderShippingSnapshot(updatedOrder),
    });

    if (hasEstimate) {
      logShippingEstimate('success', { via: 'post_patch_poll' });
      return { success: true, message: '', order: updatedOrder };
    }
  }

  // Sylius accepted the method; cart sync will pick up pricing even if GET is still stale.
  logShippingEstimate('success', {
    via: 'patch_trusted',
    methodCode: defaultMethodCode,
    ...describeOrderShippingSnapshot(updatedOrder),
  });

  return { success: true, message: '', order: updatedOrder };
}
